import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target } from 'lucide-react';
import clsx from 'clsx';
import { aaiApi } from '../../services/aaiApi';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import { useAsync } from '../aai/useAsync';
import type { AaiRequest, AaiSkill } from '../../types/aai';
import {
  Action, ErrorNote, Kicker, Loading, PageBody, PageHead, StatusChip,
  Tabs,
} from '../../components/aa';
import { relativeTime } from './format';
import { nextFor, ownerOf, stateFor } from './work';
import { OpportunitiesSection } from './OpportunitiesSection';
import { SharePanel } from '../champion/SharePanel';

/*
 * Client Projects.
 *
 * The client's Delivery Projects: scoped work with a named team and a governed
 * release, moving through stages. One list, five filters:
 *
 *   Active        everything not declined
 *   In progress   work is being done right now
 *   Waiting       held for a review or for more detail
 *   Ready to use  delivered and published for the client's people
 *   All           including declined work
 *
 * A Solution Partner reads these. Nothing on this screen decides, approves, or
 * releases anything.
 */

const FILTERS = ['Active', 'In progress', 'Waiting', 'Ready to use', 'All'] as const;
type Filter = typeof FILTERS[number];

// Each filter is a predicate over the real request states, so a filter can
// never show a row whose state does not match its label.
const MATCH: Record<Filter, (r: AaiRequest) => boolean> = {
  Active: (r) => r.status !== 'declined',
  'In progress': (r) => !r.tool_skill_id && (r.status === 'approved' || r.status === 'dept_approved'),
  Waiting: (r) => r.status === 'pending' || r.status === 'needs_info',
  'Ready to use': (r) => Boolean(r.tool_skill_id),
  All: () => true,
};

const STATE_WORD: Record<string, string> = {
  pending: 'Waiting',
  needs_info: 'Needs review',
  dept_approved: 'In progress',
  approved: 'In delivery',
  declined: 'Declined',
};

const stateWord = (r: AaiRequest): string => {
  if (r.tool_skill_id) return 'Ready to use';
  if (r.problem_stage && r.status === 'approved') return r.problem_stage;
  return STATE_WORD[r.status] || r.status;
};

// The three kinds of work on this page. All projects stacks them; a type tab
// shows one, which is where "See all" on a section head goes.
type ProjectType = 'all' | 'opportunities' | 'quick_wins' | 'delivery';

const TYPE_TABS: Array<{ id: ProjectType; label: string }> = [
  { id: 'all', label: 'All projects' },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'quick_wins', label: 'Quick Wins' },
  { id: 'delivery', label: 'Delivery Projects' },
];

const SectionHead: React.FC<{ kicker: string; title: string; definition: string; action?: React.ReactNode }> = ({ kicker, title, definition, action }) => (
  <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border-b border-line-soft pb-4">
    <div>
      <Kicker className="mb-1.5">{kicker}</Kicker>
      <h2 className="m-0 text-[22px] font-bold leading-[28px] tracking-[-0.01em] text-ink">{title}</h2>
      <p className="m-0 mt-1.5 max-w-[720px] text-[13px] leading-[21px] text-ink-dim">{definition}</p>
    </div>
    {action}
  </div>
);

const SeeAll: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button type="button" onClick={onClick} className="shrink-0 text-[14px] font-semibold text-ink transition-colors hover:text-ink-muted">
    {label}
  </button>
);

const FilterRow: React.FC<{ active: Filter; onChange: (f: Filter) => void }> = ({ active, onChange }) => (
  <div role="group" aria-label="Delivery Project status filters" className="mb-3.5 flex flex-wrap gap-1.5">
    {FILTERS.map((f) => (
      <button
        key={f}
        type="button"
        aria-pressed={active === f}
        onClick={() => onChange(f)}
        className={clsx(
          'h-11 rounded-lg px-4 text-[13.5px] font-semibold transition-colors',
          active === f && 'border border-line bg-canvas-inset text-ink',
          active !== f && 'text-ink-muted hover:bg-canvas-inset hover:text-ink',
        )}
      >
        {f}
      </button>
    ))}
  </div>
);

const Column: React.FC<{ label: string; value: React.ReactNode; foot?: React.ReactNode; grow: string }> = ({
  label, value, foot, grow,
}) => (
  <span className={clsx('hidden min-w-0 flex-col gap-1 md:flex', grow)}>
    <small className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{label}</small>
    <strong className="truncate text-[13px] font-semibold leading-[1.35] text-ink">{value}</strong>
    {foot && <em className="truncate text-[12px] not-italic text-ink-soft">{foot}</em>}
  </span>
);

const ROW = 'flex min-h-[98px] w-full items-center gap-4 border-t border-line-soft bg-white px-5 py-4 text-left first:border-t-0';

const ProjectRow: React.FC<{ request: AaiRequest }> = ({ request }) => (
  <Link to={`/partner/projects/${request.id}`} className={`${ROW} transition-colors hover:bg-canvas`}>
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-[13px] font-bold text-white">D</span>
    <span className="flex min-w-0 flex-[1.4] flex-col gap-1">
      <span className="flex items-center gap-2">
        <strong className="truncate text-[13px] font-semibold text-ink">{request.title}</strong>
        <StatusChip status={request.tool_skill_id ? 'ready' : stateFor(request)} />
      </span>
      {request.detail && <small className="truncate text-[13px] text-ink-muted">{request.detail}</small>}
      <em className="text-[12px] not-italic text-ink-soft">
        {stateWord(request)} · {request.decided_at ? `Decided ${relativeTime(request.decided_at)}` : `Raised ${relativeTime(request.created_at)}`}
      </em>
    </span>
    <Column label="Owner" value={ownerOf(request)} foot={request.department || undefined} grow="flex-[0.65]" />
    <Column label="Next" value={nextFor(request)} grow="flex-[0.8]" />
    <ArrowRight size={18} className="shrink-0 text-ink-soft" />
  </Link>
);

const ACCESS_LABEL: Record<string, string> = {
  open: 'Open to everyone',
  request: 'On request',
  restricted: 'Restricted',
};

const quickWinLine = (skill: AaiSkill): string => {
  const parts: string[] = [];
  if (skill.use_case_title) parts.push(`Delivers: ${skill.use_case_title}`);
  if (!skill.use_case_title) parts.push('Published capability');
  if (skill.created_at) parts.push(`Published ${relativeTime(skill.created_at)}`);
  return parts.join(' · ');
};

const QuickWinRow: React.FC<{ skill: AaiSkill }> = ({ skill }) => (
  <div className={ROW}>
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent-500 text-[13px] font-bold text-black">Q</span>
    <span className="flex min-w-0 flex-[1.4] flex-col gap-1">
      <span className="flex items-center gap-2">
        <strong className="truncate text-[13px] font-semibold text-ink">{skill.name}</strong>
        <StatusChip status="ready" />
      </span>
      {skill.problem && <small className="truncate text-[13px] text-ink-muted">{skill.problem}</small>}
      <em className="text-[12px] not-italic text-ink-soft">{quickWinLine(skill)}</em>
    </span>
    <Column label="Access" value={ACCESS_LABEL[skill.access_level] || skill.access_level} grow="flex-[0.65]" />
    <Column label="Owner" value={skill.created_by || 'Not recorded'} grow="flex-[0.8]" />
  </div>
);

const EmptyRows: React.FC<{ title: string; copy: string }> = ({ title, copy }) => (
  <div className="flex min-h-[330px] flex-col items-center justify-center px-10 py-10 text-center">
    <span className="grid h-12 w-12 place-items-center rounded-full bg-card text-brand-600">
      <Target size={20} strokeWidth={1.75} />
    </span>
    <h2 className="m-0 mt-4 text-[18px] font-bold text-ink">{title}</h2>
    <p className="m-0 mt-1.5 max-w-[46ch] text-[13px] leading-[21px] text-ink-dim">{copy}</p>
  </div>
);

const ProjectsPage: React.FC = () => {
  const { active, loading: clientsLoading } = useActiveClient();
  const [filter, setFilter] = useState<Filter>('Active');
  const [type, setType] = useState<ProjectType>('all');

  const requests = useAsync<{ scope: string; requests: AaiRequest[] }>(
    () => aaiApi.listRequests(), 'Could not load this client’s projects.', [active?.id],
  );
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(
    () => aaiApi.listSkills(), 'Could not load this client’s Quick Wins.', [active?.id],
  );

  if (clientsLoading) return <PageBody><Loading label="Loading your clients" /></PageBody>;
  if (!active) return <PageBody><ErrorNote message="Select an assigned client to see its projects." /></PageBody>;

  const delivery = (requests.data?.requests || []).filter(MATCH[filter]);
  const quickWins = (skills.data?.skills || []).filter((s) => s.published);

  return (
    <PageBody>
      <PageHead
        eyebrow={active.name}
        title="Client Projects"
      />

      <Tabs
        tabs={TYPE_TABS}
        active={type}
        onChange={(id) => setType(id as ProjectType)}
        label="Project type"
      />

      <div className="space-y-10">
      {(type === 'all' || type === 'opportunities') && (
        <OpportunitiesSection
          clientName={active.name}
          onChanged={requests.reload}
          action={type === 'all' && <SeeAll label="See all Opportunities" onClick={() => setType('opportunities')} />}
        />
      )}

      {(type === 'all' || type === 'quick_wins') && (
        // Where a client has no champion or trainer, the partner IS the program
        // team, so a skill somebody offered their team waits on them. It hides
        // itself when nothing is offered rather than sitting empty on the page.
        <SharePanel hideEmpty />
      )}

      {(type === 'all' || type === 'quick_wins') && (
      <section>
        <SectionHead
          kicker="Project type · Quick Win"
          title={`${active.name} Quick Wins`}
          definition="A focused result with limited setup, used and reviewed directly in the portal."
          action={type === 'all' && <SeeAll label="See all Quick Wins" onClick={() => setType('quick_wins')} />}
        />
        <div className="overflow-hidden rounded-panel border border-line bg-white">
          {skills.loading && <Loading label="Loading Quick Wins" />}
          {skills.error && <div className="px-6 py-5"><ErrorNote message={skills.error} /></div>}
          {!skills.loading && !skills.error && quickWins.length === 0 && (
            <EmptyRows title="No Quick Wins yet." copy="A Quick Win appears here once the client publishes a skill." />
          )}
          {quickWins.map((s) => <QuickWinRow key={s.id} skill={s} />)}
        </div>
      </section>
      )}

      {(type === 'all' || type === 'delivery') && (
      <section>
        <SectionHead
          kicker="Project type · Delivery Project"
          title={`${active.name} Delivery Projects`}
          definition="A larger change that needs shared ownership, scoped delivery, coordinated work, and governed release."
          action={type === 'all' && <SeeAll label="See all Delivery Projects" onClick={() => setType('delivery')} />}
        />
        <FilterRow active={filter} onChange={setFilter} />
        <div className="overflow-hidden rounded-panel border border-line bg-white">
          {requests.loading && <Loading label="Loading client projects" />}
          {requests.error && <div className="px-6 py-5"><ErrorNote message={requests.error} /></div>}
          {!requests.loading && !requests.error && delivery.length === 0 && (
            <EmptyRows title="Nothing is waiting here." copy="Change the filter. Work arrives here when the client raises a request or confirms an opportunity." />
          )}
          {delivery.map((r) => <ProjectRow key={r.id} request={r} />)}
        </div>
      </section>
      )}
      </div>
    </PageBody>
  );
};

export default ProjectsPage;
